import { useEffect, useMemo } from 'react';
import {
  ALL_MODULE_SLUGS,
  BASE_MODULE_SLUGS,
  MODULE_CATALOG,
  MODULE_GROUPS,
  MODULE_GROUP_DESCRIPTIONS,
  MODULE_GROUP_LABELS,
  defaultActiveForVertical,
  describeActivationError,
  getModule,
  readModulesDraft,
  resolveModuleActivation,
  setModuleRequested,
  validateDeactivation,
  type ModuleCatalogEntry,
  type ModuleGroup,
  type ModuleSlug,
} from '../../../lib/tenant-modules-form.js';

/**
 * Step 5 of the tenant provisioning wizard (D1.5.6) — Module activation.
 *
 * Lists the canonical 15-module catalog grouped by purpose. Base modules
 * render as locked-on chips (cannot be deactivated). Other modules are
 * checkboxes; toggling on auto-pulls transitive deps via the resolver,
 * toggling off blocks if any active dependant would lose its substrate.
 *
 * Pre-populated on first mount from `defaultActiveForVertical(vertical)`
 * — for pet-services v1: CRM + Messaging + Bookings + Payments. The
 * admin can deselect anything non-base before commit.
 *
 * The platform-truth resolver runs at commit time (D1.6.1) on the
 * server. This component mirrors the same semantics so the admin sees
 * the closure live without a server round-trip.
 */

type StepModulesProps = {
  payload: Record<string, unknown>;
  updatePayload: (patch: Record<string, unknown>) => void;
};

export function StepModules({ payload, updatePayload }: StepModulesProps): JSX.Element {
  const draft = useMemo(() => readModulesDraft(payload), [payload]);
  const vertical =
    typeof payload.vertical === 'string' ? payload.vertical : 'pet-services';

  // Seed defaults on first mount; only when no `modules` block exists yet.
  useEffect(() => {
    if (payload.modules === undefined) {
      const seeded = defaultActiveForVertical(vertical);
      updatePayload({ modules: { requested: [...seeded] } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestedSet = useMemo(() => new Set(draft.requested), [draft.requested]);
  const resolution = useMemo(
    () => resolveModuleActivation(draft.requested),
    [draft.requested],
  );
  const finalSet = useMemo(
    () => (resolution.ok ? new Set<ModuleSlug>(resolution.finalSet) : new Set<ModuleSlug>(BASE_MODULE_SLUGS)),
    [resolution],
  );
  // For UI purposes, "auto-activated" means *deps the resolver pulled in
  // because of the admin's selections* — so we exclude base modules
  // (always on regardless) since they have their own Base chip.
  const baseSet = useMemo(() => new Set<ModuleSlug>(BASE_MODULE_SLUGS), []);
  const autoActivated = useMemo(
    () => (resolution.ok ? resolution.autoActivated.filter((s) => !baseSet.has(s)) : []),
    [resolution, baseSet],
  );

  const handleToggle = (slug: ModuleSlug, nextChecked: boolean): void => {
    const entry = getModule(slug);
    if (!entry || entry.isBase) return;
    const currentDraft = readModulesDraft(payload);
    if (!nextChecked) {
      // Compute the active set the resolver would produce *now* — base + requested + auto deps.
      const currentResolution = resolveModuleActivation(currentDraft.requested);
      const activeSlugs: ReadonlyArray<ModuleSlug> = currentResolution.ok
        ? currentResolution.finalSet
        : ALL_MODULE_SLUGS.filter((s) => requestedSet.has(s) || getModule(s)?.isBase);
      const check = validateDeactivation(activeSlugs, slug);
      if (!check.ok) {
        // The user is trying to deselect their explicit request, but a
        // dependant they ALSO explicitly requested still needs it. Block
        // and surface the error inline (rendered below per-row).
        return;
      }
    }
    const next = setModuleRequested(currentDraft, slug, nextChecked);
    updatePayload({ modules: { requested: [...next.requested] } });
  };

  return (
    <div className="ferro-form" aria-label="Modules — Step 5">
      <p className="ferro-muted">
        Pick the modules to activate at provisioning. Base modules are always
        active. Selecting a module auto-activates its dependencies; clearing
        one is blocked if another active module still needs it.
      </p>

      {MODULE_GROUPS.map((group) => (
        <ModuleGroupSection
          key={group}
          group={group}
          modules={MODULE_CATALOG.filter((m) => m.group === group)}
          requestedSet={requestedSet}
          finalSet={finalSet}
          onToggle={handleToggle}
          payload={payload}
        />
      ))}

      <section
        className="ferro-wizard__modules-summary"
        aria-labelledby="step-modules-summary-heading"
        data-testid="step-modules-summary"
      >
        <h2 id="step-modules-summary-heading" className="ferro-field__label">
          Activation summary
        </h2>
        <p className="ferro-muted">
          {finalSet.size} module{finalSet.size === 1 ? '' : 's'} will be active
          on commit ({BASE_MODULE_SLUGS.length} base + {draft.requested.length}{' '}
          you selected
          {autoActivated.length > 0
            ? ` + ${autoActivated.length} auto-activated dependency${autoActivated.length === 1 ? '' : 'ies'}`
            : ''}
          ).
        </p>
        {autoActivated.length > 0 ? (
          <ul
            className="ferro-wizard__modules-chips"
            aria-label="Auto-activated modules"
            data-testid="step-modules-auto-activated"
          >
            {autoActivated.map((slug) => {
              const entry = getModule(slug);
              return (
                <li
                  key={slug}
                  className="ferro-chip ferro-chip--auto"
                  data-testid={`step-modules-auto-${slug}`}
                >
                  Auto: {entry?.displayName ?? slug}
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    </div>
  );
}

type ModuleGroupSectionProps = {
  group: ModuleGroup;
  modules: ReadonlyArray<ModuleCatalogEntry>;
  requestedSet: ReadonlySet<ModuleSlug>;
  finalSet: ReadonlySet<ModuleSlug>;
  onToggle: (slug: ModuleSlug, next: boolean) => void;
  payload: Record<string, unknown>;
};

function ModuleGroupSection({
  group,
  modules,
  requestedSet,
  finalSet,
  onToggle,
  payload,
}: ModuleGroupSectionProps): JSX.Element {
  const headingId = `step-modules-group-${group}-heading`;
  return (
    <section
      className="ferro-wizard__modules-group"
      aria-labelledby={headingId}
      data-testid={`step-modules-group-${group}`}
    >
      <h2 id={headingId} className="ferro-field__label">
        {MODULE_GROUP_LABELS[group]}
      </h2>
      <p className="ferro-muted">{MODULE_GROUP_DESCRIPTIONS[group]}</p>
      <ul className="ferro-wizard__modules">
        {modules.map((entry) => (
          <ModuleRow
            key={entry.slug}
            entry={entry}
            requested={requestedSet.has(entry.slug)}
            inFinalSet={finalSet.has(entry.slug)}
            onToggle={onToggle}
            payload={payload}
          />
        ))}
      </ul>
    </section>
  );
}

type ModuleRowProps = {
  entry: ModuleCatalogEntry;
  requested: boolean;
  inFinalSet: boolean;
  onToggle: (slug: ModuleSlug, next: boolean) => void;
  payload: Record<string, unknown>;
};

function ModuleRow({ entry, requested, inFinalSet, onToggle, payload }: ModuleRowProps): JSX.Element {
  const isBase = entry.isBase;
  // #162 — manifest-only stub (no backend/Ui): rendered non-activatable.
  const comingSoon = !entry.implemented;
  const checked = isBase ? true : comingSoon ? false : requested;
  // Auto-activated = in the resolved final set but not explicitly requested
  // and not a base module. Surfaces "this is on because of X".
  const autoActivated = !isBase && !comingSoon && !requested && inFinalSet;

  // Compute deactivation error inline so the user sees *why* clearing is
  // blocked. Only relevant when the user has the box checked and removing
  // it would orphan a dependant.
  const blockError = useMemo(() => {
    if (isBase || !requested) return null;
    const currentDraft = readModulesDraft(payload);
    const currentResolution = resolveModuleActivation(currentDraft.requested);
    if (!currentResolution.ok) return null;
    const check = validateDeactivation(currentResolution.finalSet, entry.slug);
    return check.ok ? null : describeActivationError(check.error);
  }, [isBase, requested, payload, entry.slug]);

  return (
    <li
      className={
        'ferro-wizard__modules-row'
        + (isBase ? ' ferro-wizard__modules-row--base' : '')
        + (autoActivated ? ' ferro-wizard__modules-row--auto' : '')
        + (comingSoon ? ' ferro-wizard__modules-row--coming-soon' : '')
      }
      data-testid={`step-modules-row-${entry.slug}`}
    >
      <label className="ferro-wizard__modules-toggle">
        <input
          type="checkbox"
          checked={checked}
          disabled={isBase || comingSoon}
          readOnly={isBase || comingSoon}
          onChange={(e) => onToggle(entry.slug, e.target.checked)}
          data-testid={`step-modules-toggle-${entry.slug}`}
          aria-describedby={`step-modules-desc-${entry.slug}`}
        />
        <span className="ferro-wizard__modules-name">{entry.displayName}</span>
        {isBase ? (
          <span
            className="ferro-chip ferro-chip--base"
            data-testid={`step-modules-base-${entry.slug}`}
          >
            Base
          </span>
        ) : null}
        {comingSoon ? (
          <span
            className="ferro-chip ferro-chip--coming-soon"
            data-testid={`step-modules-coming-soon-${entry.slug}`}
          >
            Coming soon
          </span>
        ) : null}
        {autoActivated ? (
          <span
            className="ferro-chip ferro-chip--auto"
            data-testid={`step-modules-row-auto-${entry.slug}`}
          >
            Auto
          </span>
        ) : null}
      </label>
      <p
        id={`step-modules-desc-${entry.slug}`}
        className="ferro-muted"
        data-testid={`step-modules-desc-${entry.slug}`}
      >
        {entry.description}
      </p>
      {entry.dependencies.length > 0 ? (
        <ul
          className="ferro-wizard__modules-deps"
          aria-label={`Dependencies of ${entry.displayName}`}
          data-testid={`step-modules-deps-${entry.slug}`}
        >
          {entry.dependencies.map((dep) => {
            const depEntry = getModule(dep);
            return (
              <li
                key={dep}
                className="ferro-chip ferro-chip--dep"
                data-testid={`step-modules-dep-${entry.slug}-${dep}`}
              >
                Needs: {depEntry?.displayName ?? dep}
              </li>
            );
          })}
        </ul>
      ) : null}
      {blockError ? (
        <span
          role="alert"
          className="ferro-form__error"
          data-testid={`step-modules-error-${entry.slug}`}
        >
          {blockError}
        </span>
      ) : null}
    </li>
  );
}
